import type { ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { FiBell, FiCreditCard, FiGrid, FiLock, FiShield, FiSliders } from 'react-icons/fi';

import { useRoles } from '@/hooks/useRoles';
import { AppearanceSettingsPage } from '@/pages/AppearanceSettingsPage';
import { DashboardSettingsPage } from '@/pages/DashboardSettingsPage';
import { CATEGORY_PARAM, NotificationsSettingsPage } from '@/pages/NotificationsSettingsPage';
import { PermissionSettingsPage } from '@/pages/PermissionSettingsPage/PermissionSettingsPage';
import { SecuritySettingsPage } from '@/pages/SecuritySettingsPage';
import { SubscriptionSettingsPage } from '@/pages/SubscriptionSettingsPage';
import type { Permission } from '@/permissions/permissions.catalog';
import { MANAGE_PERMISSIONS_RIGHTS } from '@/permissions/route-rights';
import { usePermissions } from '@/permissions/usePermissions';
import { OWNER_GROUP_ROLES, UserRole } from '@/utils/roles';

import { ROLE_PARAM } from './settings-anchors';
import { SUBSCRIPTIONS_HIDDEN } from './settings-flags';

export type SettingsSection =
  | 'dashboard'
  | 'permissions'
  | 'notifications'
  | 'security'
  | 'subscriptions'
  | 'appearance';

export type SettingsSectionDef = {
  value: SettingsSection;
  label: string;
  sub: string;
  icon: IconType;
  roles?: readonly UserRole[];
  /**
   * Rights that open the section on their own. `roles` and `rights` are alternatives, not
   * requirements: a section naming both opens for either, mirroring the services, which
   * gate on a role *or* a right rather than on both.
   */
  rights?: readonly Permission[];
  /** Hidden regardless of roles — used for env-gated sections. */
  hidden?: boolean;
  render: () => ReactNode;
};

const SECTIONS: readonly SettingsSectionDef[] = [
  {
    value: 'dashboard',
    label: 'Dashboard',
    sub: 'Widgets and layout.',
    icon: FiGrid,
    render: () => <DashboardSettingsPage embedded />,
  },
  {
    value: 'permissions',
    label: 'Roles & Permissions',
    sub: 'Access control and roles.',
    icon: FiShield,
    roles: OWNER_GROUP_ROLES,
    rights: MANAGE_PERMISSIONS_RIGHTS,
    render: () => <PermissionSettingsPage embedded />,
  },
  {
    value: 'notifications',
    label: 'Notifications',
    sub: 'Channels, delivery and alerts.',
    icon: FiBell,
    render: () => <NotificationsSettingsPage embedded />,
  },
  {
    value: 'security',
    label: 'Security',
    sub: 'Sign-in and biometrics.',
    icon: FiLock,
    render: () => <SecuritySettingsPage embedded />,
  },
  {
    value: 'subscriptions',
    label: 'Manage My Account',
    sub: 'Plans and crew seats.',
    icon: FiCreditCard,
    hidden: SUBSCRIPTIONS_HIDDEN,
    render: () => <SubscriptionSettingsPage embedded />,
  },
  {
    value: 'appearance',
    label: 'Appearance',
    sub: 'Theme.',
    icon: FiSliders,
    render: () => <AppearanceSettingsPage embedded />,
  },
];

export const useAvailableSettingsSections = (): readonly SettingsSectionDef[] => {
  const { hasAnyRole } = useRoles();
  const { canAny } = usePermissions();

  // A section naming neither roles nor rights is open to everyone; one naming both opens
  // for either, so the right alone is enough where the role is missing.
  const isOpenTo = (sec: SettingsSectionDef) =>
    (!sec.roles && !sec.rights) ||
    (!!sec.roles && hasAnyRole(sec.roles)) ||
    (!!sec.rights && canAny([...sec.rights]));

  return SECTIONS.filter((sec) => !sec.hidden && isOpenTo(sec));
};

/**
 * URL params used by sub-pages for in-section drill-down. They are cleared
 * whenever the active section changes so stale drill state never leaks across.
 */
export const SETTINGS_DRILL_PARAMS = [CATEGORY_PARAM, ROLE_PARAM] as const;

export const renderSettingsSection = (
  sections: readonly SettingsSectionDef[],
  active: SettingsSection,
): ReactNode => sections.find((sec) => sec.value === active)?.render() ?? null;
